import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { AccessManagementUserAuthContext } from '@cdw-ae/access-management-common-services';

import * as UserIdentitySettingsActions from './user-identity-settings.actions';
import {
  UserData,
  UserIdentitySettingsEntity,
} from './user-identity-settings.models';

export const USER_IDENTITY_SETTINGS_FEATURE_KEY = 'userIdentitySettings';

export interface UserIdentitySettingsState
  extends EntityState<UserIdentitySettingsEntity> {
  selectedId?: string | number; // which UserIdentitySettings record has been selected
  loaded: boolean; // has the UserIdentitySettings list been loaded
  error?: string | null; // last known error (if any)
  userData: UserData | null;

  // Permission loading state
  permissions: AccessManagementUserAuthContext | null;
  permissionsLoading: boolean;
  permissionsError?: string | null;
}

export interface UserIdentitySettingsPartialState {
  readonly [USER_IDENTITY_SETTINGS_FEATURE_KEY]: UserIdentitySettingsState;
}

export const userIdentitySettingsAdapter: EntityAdapter<UserIdentitySettingsEntity> =
  createEntityAdapter<UserIdentitySettingsEntity>();

export const initialUserIdentitySettingsState: UserIdentitySettingsState =
  userIdentitySettingsAdapter.getInitialState({
    loaded: false, // Keep if settings are loaded separately
    userData: null,
    permissions: null,
    permissionsLoading: false,
    permissionsError: null,
  });

const reducer = createReducer(
  initialUserIdentitySettingsState,
  on(UserIdentitySettingsActions.initUserIdentitySettings, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(UserIdentitySettingsActions.setUser, (state, { userData }) => ({
    ...state,
    userData: userData,
    error: null, // Clear error on successful user set/clear
  })),
  on(UserIdentitySettingsActions.setIdToken, (state, { userData }) => ({
    ...state,
    userData: userData as UserData,
    error: null, // Clear error on successful user set/clear
  })),
  on(UserIdentitySettingsActions.loadPermissions, (state) => ({
    ...state,
    permissionsLoading: true,
    permissionsError: null,
  })),
  on(UserIdentitySettingsActions.loadPermissionsSuccess, (state, { permissions }) => ({
    ...state,
    permissions: permissions,
    permissionsLoading: false,
    permissionsError: null,
  })),
  on(UserIdentitySettingsActions.loadPermissionsFailure, (state, { error }) => ({
    ...state,
    permissions: null,
    permissionsLoading: false,
    permissionsError: error,
  })),
  on(UserIdentitySettingsActions.updateCustomerSettings, (state, { timezone, dateFormat }) => ({
    ...state,
    userData: state.userData ? {
      ...state.userData,
      claims: state.userData.claims ? {
        ...state.userData.claims,
        core: {
          ...state.userData.claims.core,
          customer: {
            ...state.userData.claims.core.customer,
            timezone,
            dateFormat,
          },
        },
      } : state.userData.claims,
    } : null,
  }))
);

export function userIdentitySettingsReducer(
  state: UserIdentitySettingsState | undefined,
  action: Action
) {
  return reducer(state, action);
}
